import React, { forwardRef, useState, useMemo } from 'react';
import { X } from 'lucide-react';

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

// Color scheme with actual brand colors
const COLOR_SCHEME = {
  primary: {
    bg: '#1575DB',
    text: '#1575DB',
    border: '#1575DB',
    lightBg: '#9ac0ea',
  },
  secondary: {
    bg: '#3a4878',
    text: '#3a4878',
    border: '#3a4878',
    lightBg: '#4a6475',
  },
};

const getVariantClasses = (variant, color = 'primary') => {
  const variants = {
    default: {
      tab: `border-b-2 border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300`,
      active: `font-medium`,
    },
    contained: {
      tab: `bg-gray-100 text-gray-600 rounded-md mx-1 hover:bg-gray-200`,
      active: `text-white`,
    },
    pills: {
      tab: `bg-gray-200 text-gray-700 rounded-full px-4 py-2 transition-all hover:bg-gray-300`,
      active: `text-white`,
    },
  };

  return variants[variant] || variants.default;
};

const SIZE = {
  sm: 'text-sm h-9',
  md: 'text-base h-10',
  lg: 'text-lg h-12',
};

const TabsContext = React.createContext(null);

const Tabs = React.forwardRef(function Tabs(
  {
    children,
    defaultValue,
    value,
    onValueChange,
    variant = 'default',
    size = 'md',
    color = 'primary',
    className,
    style,
    orientation = 'horizontal',
    ...rest
  },
  ref
) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const activeValue = value !== undefined ? value : internalValue;

  const handleValueChange = (newValue) => {
    if (value === undefined) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  };

  const contextValue = useMemo(
    () => ({
      activeValue,
      onValueChange: handleValueChange,
      variant,
      size,
      color,
    }),
    [activeValue, variant, size, color]
  );

  const isVertical = orientation === 'vertical';

  return (
    <TabsContext.Provider value={contextValue}>
      <div
        ref={ref}
        className={cn(
          'w-full',
          isVertical ? 'flex' : '',
          className
        )}
        style={style}
        {...rest}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
});

Tabs.displayName = 'Tabs';

const TabsList = React.forwardRef(function TabsList(
  {
    children,
    className,
    style,
    ...rest
  },
  ref
) {
  const context = React.useContext(TabsContext);
  if (!context) {
    throw new Error('TabsList must be used within a Tabs component');
  }

  const { variant } = context;
  const isDefault = variant === 'default';

  return (
    <div
      ref={ref}
      className={cn(
        'flex overflow-x-auto',
        isDefault ? 'border-b border-gray-200' : '',
        className
      )}
      style={style}
      {...rest}
    >
      {children}
    </div>
  );
});

TabsList.displayName = 'TabsList';

const TabsTrigger = React.forwardRef(function TabsTrigger(
  {
    children,
    value,
    disabled = false,
    onClose,
    showClose = false,
    className,
    style,
    onClick: externalOnClick,
    ...rest
  },
  ref
) {
  const context = React.useContext(TabsContext);
  if (!context) {
    throw new Error('TabsTrigger must be used within a Tabs component');
  }

  const { activeValue, onValueChange, variant, size, color } = context;
  const isActive = activeValue === value;
  const colors = COLOR_SCHEME[color] || COLOR_SCHEME.primary;

  const variantClasses = getVariantClasses(variant, color);
  const sizeClasses = SIZE[size];

  // Compute inline styles based on variant and active state
  let activeStyles = {};
  if (isActive) {
    if (variant === 'default') {
      activeStyles = {
        borderBottomColor: colors.border,
        color: colors.text,
      };
    } else if (variant === 'contained' || variant === 'pills') {
      activeStyles = {
        backgroundColor: colors.bg,
        color: 'white',
      };
    }
  }

  const triggerClasses = cn(
    'px-4 py-2 font-medium transition-all whitespace-nowrap flex items-center gap-2',
    'focus:outline-none focus:ring-2 focus:ring-offset-1',
    sizeClasses,
    variantClasses.tab,
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    className
  );

  const combinedStyle = {
    ...style,
    ...activeStyles,
    ...(isActive && { outline: 'none' }),
  };

  const handleClick = (e) => {
    if (!disabled) onValueChange(value);
    externalOnClick?.(e);
  };

  return (
    <button
      ref={ref}
      role="tab"
      aria-selected={isActive}
      onClick={handleClick}
      disabled={disabled}
      className={triggerClasses}
      style={combinedStyle}
      {...rest}
    >
      {children}
      {showClose && onClose && (
        <button
          className="ml-1 text-inherit hover:opacity-70 focus:outline-none"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          type="button"
          aria-label="Close tab"
        >
          <X size={16} />
        </button>
      )}
    </button>
  );
});

TabsTrigger.displayName = 'TabsTrigger';

const TabsContent = React.forwardRef(function TabsContent(
  {
    children,
    value,
    className,
    style,
    ...rest
  },
  ref
) {
  const context = React.useContext(TabsContext);
  if (!context) {
    throw new Error('TabsContent must be used within a Tabs component');
  }

  const { activeValue } = context;

  if (activeValue !== value) {
    return null;
  }

  return (
    <div
      ref={ref}
      role="tabpanel"
      className={cn('w-full', className)}
      style={style}
      {...rest}
    >
      {children}
    </div>
  );
});

TabsContent.displayName = 'TabsContent';

export { Tabs, TabsList, TabsTrigger, TabsContent };
export default Tabs;
